import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IRecommendedBoard } from '../../../shared/components/quick-item-generator/quick-item-generator.interface';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IBoard, IBoardItemConfigurationJoins } from '../../settings/boards/boards.interface';
import {
  IFieldSetAssignmentBase,
  IFieldSetAssignmentJoins,
  TFieldSet,
} from '../../settings/fields-field-sets/field-sets/field-sets.interface';
import { IItemWorkInstructionAssignment } from '../../settings/work-instructions/work-instructions.interface';
import { IItemBase, IItemJoins, TWorkflow } from '../items.interface';
import { IBoardMembersRequest, IEditItem, TAddItem, TBoardJoins } from './items-modal.interface';

export interface IItemsModalState {
  addLoadStatus: ELoadStatus;
  boardItemConfigurations: TBoardItemConfigurationWithWorkflow[];
  boardItemConfigurationsLoading: ELoadStatus;
  boardMembers: IDropdownOption[];
  boardMembersLoading: ELoadStatus;
  reporterMembers: IDropdownOption[];
  reporterMembersLoading: ELoadStatus;
  boardOptions: IBoard[];
  boardOptionsLoading: ELoadStatus;
  shiftOptions: IDropdownOption[];
  shiftOptionsLoading: ELoadStatus;
  createdItem: IItemBase | null;
  dataForEdit: IItemJoins | null;
  dataForEditLoading: ELoadStatus;
  defaultFieldSet: TFieldSet | null;
  defaultFieldSetLoading: ELoadStatus;
  editLoadStatus: ELoadStatus;
  recommendedBoard: IRecommendedBoard | null;
  recommendedBoardLoading: ELoadStatus;
  itemWorkInstructionAssignments: IItemWorkInstructionAssignment[];
  itemWorkInstructionAssignmentLoading: ELoadStatus;
}

export type TBoardItemConfigurationWithWorkflow = IBoardItemConfigurationJoins & { workflow: TWorkflow } & {
  fieldSet: TFieldSet;
};

export type TFieldSetAssignment = IFieldSetAssignmentBase & IFieldSetAssignmentJoins;

export interface IItemCategoryOption {
  id: number;
  isActive: boolean;
  itemCategoryId: number;
  name: string;
  isDefault: boolean;
}

@Injectable()
export class ItemsModalStore extends ComponentStore<IItemsModalState> {
  public readonly boardOptions: Signal<IBoard[]> = this.selectSignal((state) => state.boardOptions);
  public readonly dataForEditBoard: Signal<IBoard | null> = this.selectSignal(
    (state) => state.dataForEdit?.board ?? null,
  );
  public readonly shiftOptions: Signal<IDropdownOption[]> = this.selectSignal((state) => state.shiftOptions);
  public readonly itemCategoryOptions: Signal<IItemCategoryOption[]> = this.selectSignal((state) => {
    return state.boardItemConfigurations.reduce((acc, boardItemConfiguration: TBoardItemConfigurationWithWorkflow) => {
      if (boardItemConfiguration.boardId) {
        acc.push({
          id: boardItemConfiguration.id,
          isActive: boardItemConfiguration.itemCategory.isActive,
          isDefault: boardItemConfiguration.itemCategory?.isDefault,
          itemCategoryId: boardItemConfiguration.itemCategoryId,
          name: boardItemConfiguration.itemCategory?.isDefault
            ? this.translate.instant(`system.label.${boardItemConfiguration.itemCategory?.name}`)
            : boardItemConfiguration.itemCategory?.name,
        });
      }

      return acc;
    }, [] as IItemCategoryOption[]);
  });
  public readonly boardItemConfigurations: Signal<TBoardItemConfigurationWithWorkflow[]> = this.selectSignal(
    (state) => {
      return state.boardItemConfigurations;
    },
  );
  public readonly boardItemConfigurationsLoading: Signal<ELoadStatus> = this.selectSignal((state) => {
    return state.boardItemConfigurationsLoading;
  });
  public readonly boardMembers: Signal<IDropdownOption[]> = this.selectSignal((state) => state.boardMembers);
  public readonly reporterMembers: Signal<IDropdownOption[]> = this.selectSignal((state) => state.reporterMembers);
  public readonly recommendedBoard$ = this.select((state) => state.recommendedBoard);
  public readonly isDataForEditLoading$ = this.select((state) => state.dataForEditLoading).pipe(
    map((status) => status === ELoadStatus.loading),
  );

  readonly addItemData = this.effect((trigger$: Observable<TAddItem>) =>
    trigger$.pipe(
      switchMap((item: TAddItem) => {
        this.patchState({ addLoadStatus: ELoadStatus.loading, createdItem: null });

        return this.addItem(item).pipe(
          tapResponse(
            (response: IBaseResponse<IItemBase>): void => {
              this.patchState({ addLoadStatus: ELoadStatus.success, createdItem: response.data });
            },
            // eslint-disable-next-line no-console
            () => {
              this.patchState({ addLoadStatus: ELoadStatus.failure });
            },
          ),
        );
      }),
    ),
  );

  readonly editItemData = this.effect((trigger$: Observable<IEditItem>) =>
    trigger$.pipe(
      switchMap((editItem: IEditItem) => {
        this.patchState({ editLoadStatus: ELoadStatus.loading });

        return this.editItem(editItem).pipe(
          tapResponse(
            (_) => this.patchState({ editLoadStatus: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            () => {
              this.patchState({ editLoadStatus: ELoadStatus.failure });
            },
          ),
        );
      }),
    ),
  );

  readonly getItemForEdit = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ dataForEditLoading: ELoadStatus.loading });
        const httpParams = this.httpUtilities.insertGenericCrudRequestParameters({
          join: [
            'board||name,aiAgentId,oeeLineId',
            'boardItemConfiguration||name,itemType,ishikawaTemplateId',
            'boardItemConfiguration.itemCategory||name,isDefault,isActive',
            'currentWorkflowStep||name,isDefault,statusCategory',
            'assignee||name',
            'reporter||name',
            'boardItemConfiguration.workflow||id,name,startWorkflowStepId',
            'boardItemConfiguration.workflow.workflowStepAssignments||id',
            'boardItemConfiguration.workflow.workflowStepAssignments.workflowStep||name,isDefault,statusCategory',
            'boardItemConfiguration.workflow.workflowStepAssignments.nextWorkflowStep||name,isDefault,statusCategory',
            'itemRecurrence',
            'escalatedBoard||name',
          ],
        });

        return this.getOneItem(httpParams, id).pipe(
          tapResponse(
            (response) => this.patchState({ dataForEdit: response.data, dataForEditLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadBoardOptions = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ boardOptionsLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getBoardDropdownOptions(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<IBoard>): void => {
              this.patchState({
                boardOptions: output.data,
                boardOptionsLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadShiftOptions = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ shiftOptionsLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getShiftDropdownOptions(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<IDropdownOption>): void => {
              this.patchState({
                shiftOptions: output.data,
                shiftOptionsLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadBoardItemConfigurations = this.effect(
    (trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
      trigger$.pipe(
        switchMap((params: IGenericCrudRequestConstructionParameters) => {
          this.patchState({ boardItemConfigurationsLoading: ELoadStatus.loading });
          const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

          return this.getItemConfigurations(httpParams).pipe(
            tapResponse(
              (output: IGetManyResponse<TBoardItemConfigurationWithWorkflow>): void => {
                this.patchState({
                  boardItemConfigurations: output.data,
                  boardItemConfigurationsLoading: ELoadStatus.success,
                });
              },
              // eslint-disable-next-line no-console
              (error) => console.error('Error loading data', error),
            ),
          );
        }),
      ),
  );

  readonly loadBoardMembers = this.effect((trigger$: Observable<IBoardMembersRequest>) =>
    trigger$.pipe(
      switchMap((params: IBoardMembersRequest) => {
        this.patchState({ boardMembersLoading: ELoadStatus.loading });

        let httpParams: HttpParams = new HttpParams();

        if (params.search) {
          httpParams = httpParams.append('search', params.search);
        }

        if (params.selectedUserId) {
          httpParams = httpParams.append('selectedUser', params.selectedUserId.toString());
        }

        if (params.escalatedBoardId) {
          httpParams = httpParams.append('escalatedBoardId', params.escalatedBoardId.toString());
        }

        return this.getBoardMembers(params.boardId, httpParams).pipe(
          tapResponse(
            (response): void => {
              this.patchState({
                boardMembers: response.data,
                boardMembersLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadReporterMembers = this.effect((trigger$: Observable<IBoardMembersRequest>) =>
    trigger$.pipe(
      switchMap((params: IBoardMembersRequest) => {
        this.patchState({ reporterMembersLoading: ELoadStatus.loading });

        let httpParams: HttpParams = new HttpParams();

        if (params.search) {
          httpParams = httpParams.append('search', params.search);
        }

        if (params.selectedUserId) {
          httpParams = httpParams.append('selectedUser', params.selectedUserId.toString());
        }

        if (params.escalatedBoardId) {
          httpParams = httpParams.append('escalatedBoardId', params.escalatedBoardId.toString());
        }

        return this.getBoardMembers(params.boardId, httpParams).pipe(
          tapResponse(
            (response): void => {
              this.patchState({
                reporterMembers: response.data,
                reporterMembersLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadDefaultFieldSet = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ defaultFieldSetLoading: ELoadStatus.loading });
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getDefaultFieldSet(httpParams).pipe(
          tapResponse(
            (output: IGetManyResponse<TFieldSet>): void => {
              this.patchState({
                defaultFieldSet: output.data[0],
                defaultFieldSetLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly getRecommendedBoardData = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() => {
        this.patchState({ recommendedBoard: null, recommendedBoardLoading: ELoadStatus.loading });

        return this.getRecommendedBoard().pipe(
          tapResponse(
            (response): void => {
              this.patchState({
                recommendedBoard: response.data,
                recommendedBoardLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
    private readonly translate: TranslateService,
  ) {
    super({
      addLoadStatus: ELoadStatus.initial,
      boardItemConfigurations: [],
      boardItemConfigurationsLoading: ELoadStatus.initial,
      boardMembers: [],
      boardMembersLoading: ELoadStatus.initial,
      boardOptions: [],
      boardOptionsLoading: ELoadStatus.initial,
      createdItem: null,
      dataForEdit: null,
      dataForEditLoading: ELoadStatus.initial,
      defaultFieldSet: null,
      defaultFieldSetLoading: ELoadStatus.initial,
      editLoadStatus: ELoadStatus.initial,
      itemWorkInstructionAssignmentLoading: ELoadStatus.initial,
      itemWorkInstructionAssignments: [],
      recommendedBoard: null,
      recommendedBoardLoading: ELoadStatus.initial,
      reporterMembers: [],
      reporterMembersLoading: ELoadStatus.initial,
      shiftOptions: [],
      shiftOptionsLoading: ELoadStatus.initial,
    });
  }

  private addItem(body: TAddItem): Observable<IBaseResponse<IItemBase>> {
    return this.http.post<IBaseResponse<IItemBase>>('items', body);
  }

  private editItem(editItem: IEditItem): Observable<IBaseResponse<IItemBase>> {
    return this.http.patch<IBaseResponse<IItemBase>>(`items/${editItem.id}`, editItem.dto);
  }

  private getBoardDropdownOptions(params: HttpParams): Observable<IGetManyResponse<IBoard>> {
    return this.http.get<IGetManyResponse<IBoard>>('boards', { params });
  }

  private getShiftDropdownOptions(params: HttpParams): Observable<IGetManyResponse<IDropdownOption>> {
    return this.http.get<IGetManyResponse<IDropdownOption>>('external-dropdowns/shifts/-1', { params });
  }

  private getItemConfigurations(params: HttpParams): Observable<IGetManyResponse<TBoardItemConfigurationWithWorkflow>> {
    return this.http.get<IGetManyResponse<TBoardItemConfigurationWithWorkflow>>('board-item-configurations', {
      params,
    });
  }

  private getBoardMembers(boardId: number, params: HttpParams): Observable<IGetManyResponse<TBoardJoins>> {
    return this.http.get<IGetManyResponse<TBoardJoins>>(`boards/${boardId}/members`, { params });
  }

  private getOneItem(params: HttpParams, id: number): Observable<IBaseResponse<IItemJoins>> {
    return this.http.get<IBaseResponse<IItemJoins>>(`items/${id}`, { params });
  }

  private getDefaultFieldSet(params: HttpParams): Observable<IGetManyResponse<TFieldSet>> {
    return this.http.get<IGetManyResponse<TFieldSet>>('field-sets', { params });
  }

  private getRecommendedBoard(): Observable<IBaseResponse<IRecommendedBoard>> {
    return this.http.get<IBaseResponse<IRecommendedBoard>>('users/recommended-board');
  }
}
